<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-6 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">{{ $t("pos.menu.products") }}</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
				<div class="col-lg-6 col-5 text-right">
					<base-button size="md" type="neutral" @click="openForm(null)">{{ $t("pos.product.add") }}</base-button>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<pos-install-alert :show="notInstalled"></pos-install-alert>
			<card class="no-border-card" body-classes="px-0 pb-1">
				<div class="row px-4 pb-3">
					<div class="col-md-5">
						<el-input v-model="q" clearable :placeholder="$t('pos.product.search')" @keyup.enter.native="load" @clear="load"></el-input>
					</div>
					<div class="col-md-3">
						<el-select v-model="status" class="w-100" @change="load">
							<el-option :label="$t('pos.product.status_all')" value=""></el-option>
							<el-option :label="$t('pos.common.active')" value="1"></el-option>
							<el-option :label="$t('pos.common.inactive')" value="0"></el-option>
						</el-select>
					</div>
					<div class="col-md-2">
						<base-button type="primary" @click="load">{{ $t("pos.common.search") }}</base-button>
					</div>
				</div>
				<el-table :data="rows" v-loading="loading" header-row-class-name="thead-light" class="bon-table pos-table">
					<el-table-column :label="$t('pos.product.image')" width="76" align="center">
						<template slot-scope="{ row }">
							<img v-if="row.image" :src="imgUrl(row.image)" class="pos-thumb" alt="" />
							<span v-else class="pos-thumb pos-thumb-empty"></span>
						</template>
					</el-table-column>
					<el-table-column :label="$t('pos.product.name')" prop="name" min-width="200"></el-table-column>
					<el-table-column :label="$t('pos.product.code')" width="110">
						<template slot-scope="{ row }">{{ row.code || "" }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.product.price')" width="120" align="right">
						<template slot-scope="{ row }"><strong>{{ money(row.price) }}</strong></template>
					</el-table-column>
					<el-table-column :label="$t('pos.product.wms')" prop="wms_summary" min-width="200"></el-table-column>
					<el-table-column :label="$t('pos.product.used_in_events')" width="110" align="right">
						<template slot-scope="{ row }">{{ row.event_count }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.product.device_sets')" width="140" align="right">
						<template slot-scope="{ row }">{{ row.device_count }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.product.status')" width="120">
						<template slot-scope="{ row }">
							<el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">{{ row.status === 1 ? $t("pos.common.active") : $t("pos.common.inactive") }}</el-tag>
						</template>
					</el-table-column>
					<!-- row menu pinned at the right edge, same as /backoffice/orders-overview
					     (OrdersNew.vue): the popper is appended to <body>, so it opens from the
					     fixed-right copy too -->
					<el-table-column :label="$t('actions')" width="64" fixed="right" align="center" class-name="bon-tight">
						<template slot-scope="{ row }">
							<el-dropdown trigger="click" placement="bottom-end" @command="(c) => onCommand(c, row)">
								<base-button size="sm" type="secondary" icon data-testid="pos-row-menu" :aria-label="$t('pos.common.menu')" :title="$t('pos.common.menu')">
									<i class="fa fa-ellipsis-v"></i>
								</base-button>
								<el-dropdown-menu slot="dropdown" class="pos-row-menu">
									<el-dropdown-item command="edit"><i class="fa fa-pen text-blue mr-2"></i>{{ $t("pos.product.edit") }}</el-dropdown-item>
									<el-dropdown-item command="delete" :disabled="row.sold" divided>
										<span class="text-danger">
											<i class="fa fa-trash mr-2"></i>
											<el-tooltip v-if="row.sold" :content="$t('pos.product.sold_hint')" placement="left">
												<span>{{ $t("pos.product.delete") }}</span>
											</el-tooltip>
											<span v-else>{{ $t("pos.product.delete") }}</span>
										</span>
									</el-dropdown-item>
								</el-dropdown-menu>
							</el-dropdown>
						</template>
					</el-table-column>
				</el-table>
			</card>
		</div>

		<el-dialog :title="form.id ? $t('pos.product.title_edit') : $t('pos.product.title_add')" :visible.sync="formVisible" width="760px" top="5vh">
			<!-- 1. image -->
			<div class="form-group">
				<label class="form-control-label d-block">{{ $t("pos.product.image_label") }}</label>
				<div class="d-flex align-items-start">
					<div class="pos-product-preview mr-3">
						<img v-if="previewSrc" :src="previewSrc" alt="" />
					</div>
					<div>
						<label class="btn btn-sm btn-outline-primary mb-2">
							{{ $t("pos.product.image_upload") }}
							<input type="file" accept="image/png,image/jpeg" class="d-none" @change="onImage" />
						</label>
						<div v-if="previewSrc">
							<base-button size="sm" type="link" class="text-danger px-0" @click="removeImage">{{ $t("pos.product.image_remove") }}</base-button>
						</div>
						<small class="text-muted d-block">{{ $t("pos.product.image_hint") }}</small>
					</div>
				</div>
			</div>
			<div class="row">
				<!-- 2–4 -->
				<div class="col-md-6"><base-input :label="$t('pos.product.name') + ' *'" v-model="form.name" maxlength="150" :error="fieldErr('name')"></base-input></div>
				<div class="col-md-3"><base-input :label="$t('pos.product.code')" v-model="form.code" maxlength="30" :error="fieldErr('code')"></base-input></div>
				<div class="col-md-3 form-group">
					<label class="form-control-label d-block">{{ $t("pos.product.price_baht") }} *</label>
					<el-input-number v-model="form.price" :min="0" :max="9999999.99" :precision="2" :controls="false" class="w-100"></el-input-number>
				</div>
				<!-- 5–6 -->
				<div class="col-md-4 form-group">
					<label class="form-control-label d-block">{{ $t("pos.product.status") }}</label>
					<el-radio-group v-model="form.status">
						<el-radio :label="1">{{ $t("pos.common.active") }}</el-radio>
						<el-radio :label="0">{{ $t("pos.common.inactive") }}</el-radio>
					</el-radio-group>
				</div>
				<div class="col-md-8"><base-input :label="$t('pos.product.note')" v-model="form.note" maxlength="255"></base-input></div>
			</div>
			<!-- 7. WMS lines -->
			<h5 class="mb-2">{{ $t("pos.product.wms_section") }} *</h5>
			<div v-for="(it, i) in form.items" :key="it.key" class="d-flex align-items-center mb-2">
				<el-select
					v-model="it.wms_product_id"
					filterable
					remote
					class="flex-grow-1 mr-2"
					:remote-method="searchWms"
					:loading="wmsLoading"
					:placeholder="$t('pos.product.wms_search')"
					@focus="() => !wmsOptions.length && searchWms('')"
					@change="(id) => pickWms(it, id)"
				>
					<el-option v-if="it.wms_product_id && !wmsOptions.some((o) => o.id === it.wms_product_id)" :key="'cur' + it.wms_product_id" :label="wmsLabel(it)" :value="it.wms_product_id"></el-option>
					<el-option v-for="o in wmsOptions" :key="o.id" :label="wmsLabel(o)" :value="o.id" :disabled="takenWms(o.id, it)"></el-option>
				</el-select>
				<el-input-number v-model="it.qty" :min="1" :max="999" size="small" class="pos-qty mr-2"></el-input-number>
				<el-tag v-if="it.wms_missing" type="danger" size="mini" class="mr-2">{{ $t("pos.product.wms_missing") }}</el-tag>
				<base-button size="sm" type="link" class="text-danger" @click="form.items.splice(i, 1)">✕</base-button>
			</div>
			<base-button size="sm" type="secondary" :disabled="form.items.length >= 20" @click="addLine">{{ $t("pos.product.wms_add") }}</base-button>
			<div class="d-flex justify-content-between mt-2">
				<small class="text-muted">{{ $t("pos.product.wms_hint") }}</small>
				<small>{{ $t("pos.product.wms_footer", { n: totals.lines, q: totals.pieces }) }}</small>
			</div>
			<span slot="footer">
				<base-button type="secondary" @click="formVisible = false">{{ $t("pos.common.cancel") }}</base-button>
				<base-button type="primary" :disabled="saving || invalid.length > 0" @click="save">{{ $t("pos.common.save") }}</base-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
import swal from "sweetalert2";
import { Table, TableColumn, Tag, Dialog, RadioGroup, Radio, InputNumber, Input, Select, Option, Dropdown, DropdownMenu, DropdownItem, Tooltip } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { PosService, WmsService } from "@/resource";
import {
	posMoney,
	posProductImageUrl,
	posResizeImageFile,
	posDataUrlBytes,
	posWmsTotals,
	posProductBody,
	posProductInvalid,
	POS_PRODUCT_IMAGE_MAX_SIDE,
	POS_PRODUCT_IMAGE_MAX_BYTES
} from "@/utils/pos";
import posPageMixin from "./posPageMixin";
import PosInstallAlert from "./PosInstallAlert.vue";

let lineSeq = 0;
const line = (o) => ({ key: ++lineSeq, wms_product_id: null, name: "", man_pn: "", qty: 1, wms_missing: false, ...(o || {}) });
const emptyForm = () => ({ id: null, name: "", code: "", price: undefined, status: 1, note: "", image: "", image_url_path: "", image_data_url: "", image_remove: false, items: [line()] });

export default {
	name: "PosProducts",
	mixins: [posPageMixin],
	components: {
		RouteBreadCrumb,
		PosInstallAlert,
		[Table.name]: Table,
		[TableColumn.name]: TableColumn,
		[Tag.name]: Tag,
		[Dialog.name]: Dialog,
		[RadioGroup.name]: RadioGroup,
		[Radio.name]: Radio,
		[InputNumber.name]: InputNumber,
		[Input.name]: Input,
		[Select.name]: Select,
		[Option.name]: Option,
		[Dropdown.name]: Dropdown,
		[DropdownMenu.name]: DropdownMenu,
		[DropdownItem.name]: DropdownItem,
		[Tooltip.name]: Tooltip
	},
	data() {
		return {
			rows: [],
			q: "",
			status: "",
			formVisible: false,
			form: emptyForm(),
			saving: false,
			serverBad: [],
			wmsOptions: [],
			wmsLoading: false,
			apiBase: process.env.VUE_APP_API_BASE_URL || ""
		};
	},
	computed: {
		totals() {
			return posWmsTotals(this.form.items.filter((i) => i.wms_product_id));
		},
		invalid() {
			return posProductInvalid(this.form);
		},
		previewSrc() {
			if (this.form.image_data_url) return this.form.image_data_url;
			if (this.form.image_remove || !this.form.image) return "";
			return this.imgUrl(this.form.image);
		}
	},
	mounted() {
		this.load();
	},
	methods: {
		money: posMoney,
		imgUrl(name) {
			return posProductImageUrl(this.apiBase, name);
		},
		fieldErr(f) {
			return this.serverBad.indexOf(f) !== -1 ? this.$t("pos.errors.pos_product_invalid") : "";
		},
		wmsLabel(o) {
			return [o.man_pn, o.name].filter((x) => x).join(" · ");
		},
		takenWms(id, cur) {
			return this.form.items.some((i) => i !== cur && i.wms_product_id === id);
		},
		async load() {
			this.loading = true;
			const params = {};
			if (this.q.trim()) params.q = this.q.trim();
			if (this.status !== "") params.status = this.status;
			try {
				const res = await PosService.posProductList(params);
				this.rows = (res.data && res.data.Data) || [];
				this.notInstalled = false;
			} catch (err) {
				this.rows = [];
				this.posError(err);
			} finally {
				this.loading = false;
			}
		},
		async searchWms(q) {
			this.wmsLoading = true;
			try {
				const res = await WmsService.ProductOptions({ search: q || "" });
				const body = (res && res.data) || {};
				const datum = body.Datum || (Array.isArray(body.Data) ? body.Data[0] : body.Data) || {};
				this.wmsOptions = (datum.items || []).filter((r) => r && r.id);
			} catch (e) {
				this.wmsOptions = [];
			} finally {
				this.wmsLoading = false;
			}
		},
		pickWms(it, id) {
			const o = this.wmsOptions.find((x) => x.id === id);
			if (o) {
				it.name = o.name;
				it.man_pn = o.man_pn;
				it.wms_missing = false;
			}
		},
		addLine() {
			if (this.form.items.length < 20) this.form.items.push(line());
		},
		onCommand(cmd, row) {
			if (cmd === "edit") this.openForm(row);
			else if (cmd === "delete" && !row.sold) this.remove(row);
		},
		async openForm(row) {
			this.serverBad = [];
			if (!row) {
				this.form = emptyForm();
				this.formVisible = true;
				return;
			}
			try {
				const res = await PosService.posProductGet(row.id);
				const d = (res.data && res.data.Datum) || {};
				this.form = {
					id: d.id,
					name: d.name || "",
					code: d.code || "",
					price: Number(d.price),
					status: d.status === 0 ? 0 : 1,
					note: d.note || "",
					image: d.image || "",
					image_url_path: d.image_url_path || "",
					image_data_url: "",
					image_remove: false,
					items: (d.items || []).map((i) => line({ wms_product_id: i.wms_product_id, name: i.name, man_pn: i.man_pn, qty: i.qty, wms_missing: !!i.wms_missing }))
				};
				if (!this.form.items.length) this.form.items.push(line());
				this.formVisible = true;
			} catch (err) {
				this.posError(err);
			}
		},
		async onImage(e) {
			const file = e.target.files && e.target.files[0];
			e.target.value = "";
			if (!file) return;
			try {
				const url = await posResizeImageFile(file, POS_PRODUCT_IMAGE_MAX_SIDE, "image/jpeg", 0.85);
				if (posDataUrlBytes(url) > POS_PRODUCT_IMAGE_MAX_BYTES) {
					swal.fire({ title: this.$t("pos.product.image_too_big"), icon: "warning" });
					return;
				}
				this.form.image_data_url = url;
				this.form.image_remove = false;
			} catch (err) {
				swal.fire({ title: this.$t("pos.errors.pos_image_invalid"), icon: "warning" });
			}
		},
		removeImage() {
			this.form.image_data_url = "";
			this.form.image_remove = true;
		},
		async save() {
			if (this.invalid.length) return;
			this.saving = true;
			this.serverBad = [];
			const body = posProductBody(this.form);
			try {
				if (this.form.id) await PosService.posProductUpdate(this.form.id, body);
				else await PosService.posProductCreate(body);
				this.formVisible = false;
				this.posToast("pos.common.saved");
				this.load();
			} catch (err) {
				const r = err && err.response;
				if (r && r.data && r.data.Msg === "pos_product_invalid") this.serverBad = String(r.data.Cause || "").split(",");
				this.posError(err);
			} finally {
				this.saving = false;
			}
		},
		async remove(row) {
			if (!(await this.posConfirm(this.$t("pos.product.confirm_delete")))) return;
			try {
				await PosService.posProductDelete(row.id);
				this.posToast("pos.common.deleted");
				this.load();
			} catch (err) {
				this.posError(err);
			}
		}
	}
};
</script>

<style>
.pos-thumb {
	width: 48px;
	height: 48px;
	object-fit: cover;
	border-radius: 6px;
	display: inline-block;
	vertical-align: middle;
}
.pos-thumb-empty {
	background: #e9ecef;
}
.pos-product-preview {
	width: 192px;
	height: 120px; /* 16:10 */
	border-radius: 8px;
	background: #e9ecef;
	overflow: hidden;
	flex: none;
}
.pos-product-preview img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}
.pos-qty {
	width: 120px;
}
</style>
